import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_TITLES = {
  pasta: "Pasta", wings: "Wings", sides: "Sides", desserts: "Desserts", drinks: "Drinks", extras: "Extras",
};

export default function AdminMenu() {
  const [menu, setMenu] = useState(null);
  const [allergenLabels, setAllergenLabels] = useState({});
  const [edits, setEdits] = useState({}); // item_id -> Set of allergen keys
  const [saving, setSaving] = useState({});
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/menu");
      setMenu(data.menu);
      setAllergenLabels(data.allergen_labels);
      const initial = {};
      Object.values(data.menu).flat().forEach((it) => { initial[it.id] = new Set(it.allergens || []); });
      setEdits(initial);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("lsk_admin_token"); nav("/admin/login"); }
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("lsk_admin_token")) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const toggle = (itemId, key) => {
    setEdits((prev) => {
      const next = { ...prev };
      const set = new Set(next[itemId] || []);
      if (set.has(key)) set.delete(key); else set.add(key);
      next[itemId] = set;
      return next;
    });
  };

  const save = async (item) => {
    setSaving((s) => ({ ...s, [item.id]: true }));
    try {
      await api.patch(`/admin/menu/${item.id}/allergens`, { allergens: Array.from(edits[item.id] || []) });
      toast.success(`Saved — ${item.name}`);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving((s) => ({ ...s, [item.id]: false }));
    }
  };

  if (!menu) return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="min-h-screen bg-ink text-cream">
      <header className="border-b border-border bg-black/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="font-heading text-cream uppercase tracking-tight text-xl">
            <span className="text-gold">LSK</span> Menu Editor — Allergens
          </div>
          <Link to="/admin/orders" className="btn-outline-poster !px-3 !py-2 !text-sm" data-testid="back-to-orders">
            <ArrowLeft className="h-4 w-4 mr-1 inline" /> Orders
          </Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {Object.entries(menu).map(([cat, items]) => (
          <section key={cat} className="poster-card p-5">
            <h2 className="font-heading uppercase tracking-tight text-cream text-3xl mb-4">{CATEGORY_TITLES[cat] || cat}</h2>
            <div className="space-y-3">
              {items.map((it) => (
                <div key={it.id} className="border border-border p-4" data-testid={`admin-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading uppercase tracking-tight text-cream text-xl leading-none">{it.name}</div>
                      <div className="text-cream/50 text-xs mt-1">£{it.price.toFixed(2)}</div>
                    </div>
                    <button onClick={() => save(it)} disabled={saving[it.id]} className="btn-poster !text-sm !px-3 !py-2" data-testid={`save-${it.id}-btn`}>
                      {saving[it.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1 inline" /> Save</>}
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {Object.entries(allergenLabels).map(([key, label]) => {
                      const active = edits[it.id]?.has(key);
                      return (
                        <button
                          key={key}
                          onClick={() => toggle(it.id, key)}
                          className={`px-2 py-1 text-xs font-heading uppercase tracking-widest border-2 ${active ? "border-gold bg-gold text-black" : "border-border text-cream/60 hover:border-gold/60"}`}
                          data-testid={`allergen-${it.id}-${key}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
